import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, FileText, Filter } from 'lucide-react';
import { chapterAPI, novelAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

const ChapterList = () => {
  const [chapters, setChapters] = useState([]);
  const [novels, setNovels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNovel, setSelectedNovel] = useState('');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchNovels();
  }, []);

  useEffect(() => {
    if (selectedNovel) {
      fetchChapters();
    } else {
      setChapters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNovel, currentPage]);

  const fetchNovels = async () => {
    try {
      const response = await novelAPI.getAll({ limit: 100 });
      setNovels(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChapters = async () => {
    setIsLoading(true);
    try {
      const response = await chapterAPI.getList(selectedNovel, { page: currentPage, limit: 30 });
      setChapters(response.data || []);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to load chapters');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (novelId, chapterId, chapterNum) => {
    if (!window.confirm(`Are you sure you want to delete Chapter ${chapterNum}?`)) return;
    
    try {
      await chapterAPI.delete(novelId, chapterId);
      toast.success('Chapter deleted');
      fetchChapters();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="chapter-list-admin">
      <div className="admin-page-header">
        <h1>Chapter Management</h1>
        <Link to="/admin/chapters/new" className="btn-add">
          <Plus size={18} />
          Add Chapter
        </Link>
      </div>

      <div className="admin-filters">
        <div className="filter-group" style={{ minWidth: '300px' }}>
          <label style={{ marginRight: '12px', color: 'var(--text-secondary)' }}>Select Novel:</label>
          <select
            value={selectedNovel}
            onChange={(e) => { setSelectedNovel(e.target.value); setCurrentPage(1); }}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)'
            }}
          >
            <option value="">-- Select Novel --</option>
            {novels.map((novel) => (
              <option key={novel._id} value={novel._id}>{novel.title}</option>
            ))}
          </select>
        </div>
      </div>

      {!selectedNovel ? (
        <div className="admin-empty">
          <Filter size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>Please select a novel to view chapters</p>
        </div>
      ) : isLoading ? (
        <div className="admin-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : chapters.length > 0 ? (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Chapter</th>
                  <th>Title</th>
                  <th>Words</th>
                  <th>Views</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {chapters.map((chapter) => (
                  <tr key={chapter._id}>
                    <td>
                      <strong>Chapter {chapter.chapterNumber}</strong>
                    </td>
                    <td>{chapter.title || '-'}</td>
                    <td>{chapter.wordCount?.toLocaleString() || 0}</td>
                    <td>{chapter.views?.toLocaleString() || 0}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      {new Date(chapter.createdAt).toLocaleDateString('en-US')}
                    </td>
                    <td>
                      <div className="table-actions">
                        <Link to={`/admin/chapters/edit/${chapter._id}`} className="btn-edit" title="Edit">
                          <Edit size={16} />
                        </Link>
                        <button 
                          className="btn-delete"
                          title="Delete"
                          onClick={() => handleDelete(selectedNovel, chapter._id, chapter.chapterNumber)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="admin-pagination">
              {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-btn ${page === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="admin-empty">
          <FileText size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>No chapters in this novel</p>
          <Link to="/admin/chapters/new" style={{ color: 'var(--primary)', marginTop: '12px' }}>
            Add first chapter →
          </Link>
        </div>
      )}
    </div>
  );
};

export default ChapterList;
